import { useSettings, useStore } from '../state/store'
import { languageOf } from '../lib/languages'
import { levelFor, levelName, MAX_LEVEL, MIN_LEVEL, stepLabel } from '../lib/levels'

/**
 * The level, in the rail, on every screen.
 *
 * It began in Settings, which is the right home for the explanation and the
 * wrong home for the act. Level is not a preference you set once like a theme —
 * it is something a learner moves during a session: warm up on HSK 2 where
 * everything comes out without thinking, then step to 3 for the work. Four taps
 * through Settings and back is enough friction that nobody does it, and an
 * island list that only ever shows one difficulty is the problem this feature
 * was built to solve, one level higher up.
 *
 * Six steps rather than the three tiers the sign-up form asks for. That
 * question is "who are you", answered once by someone who has not seen the
 * product yet; this is "what am I drilling right now", and it needs the real
 * scale — the one a Mandarin learner already knows their own place on.
 *
 * Same shape as the speech slider directly above it, for the same reason: one
 * control that governs every screen, reachable from all of them.
 */
export function LevelPicker() {
  const settings = useSettings()
  const { dispatch } = useStore()
  const lang = languageOf(settings.activeLanguage)
  const current = levelFor(settings, lang.code)

  const steps = []
  for (let n = MIN_LEVEL; n <= MAX_LEVEL; n++) steps.push(n)

  return (
    <div className="level">
      <div className="level-row">
        <span className="speed-label">{levelName(lang.code)}</span>
        <div className="level-steps" role="group" aria-label={`${lang.name} level`}>
          {steps.map((n) => (
            <button
              key={n}
              type="button"
              className={`level-step ${n === current ? 'is-on' : ''}`}
              aria-pressed={n === current}
              // The full name in the label: "3" alone tells a screen reader
              // nothing about what scale it belongs to.
              aria-label={`${levelName(lang.code)} ${stepLabel(lang.code, n)}`}
              onClick={() =>
                dispatch({
                  type: 'updateSettings',
                  patch: {
                    levelByLanguage: { ...settings.levelByLanguage, [lang.code]: n },
                  },
                })
              }
            >
              {stepLabel(lang.code, n)}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
